import { Building2, FileSpreadsheet, Handshake, User, type LucideIcon } from "lucide-react";

import { Badge, type BadgeProps } from "@/components/ui/badge";
import { CommandItem } from "@/components/ui/command";
import { formatMoneyCompact } from "@/lib/format";
import { DEAL_STATE_LABELS, type DealState } from "@/types/deals";
import type { SearchResult, SearchResultKind } from "@/types/search";

const KIND_ICON: Record<SearchResultKind, LucideIcon> = {
  customer: Building2,
  deal: Handshake,
  contact: User,
  account: FileSpreadsheet,
};

const KIND_LABEL: Record<SearchResultKind, string> = {
  customer: "Client",
  deal: "Deal",
  contact: "Contact",
  account: "Account",
};

// Stage badge tone. Terminal states get win/loss colors; live stages that need
// action read as `warning` (orange); early stages stay neutral.
const STAGE_VARIANT: Partial<Record<DealState, BadgeProps["variant"]>> = {
  closed_won: "success",
  closed_lost: "destructive",
  bid_requested: "warning",
  proposed: "warning",
  awaiting_signature: "warning",
};

/** Join the non-empty parts of a sub-line with a middle dot. */
function joinDot(parts: Array<string | null | undefined>): string {
  return parts.filter((p): p is string => !!p && p.trim() !== "").join(" · ");
}

function subline(r: SearchResult): string {
  switch (r.kind) {
    case "customer": {
      const location = [r.city, r.region].filter(Boolean).join(", ");
      return joinDot([r.industry, location]);
    }
    case "deal":
      return joinDot([
        r.customer_name,
        r.matched_salesforce_contract_number
          ? `Salesforce contract ${r.matched_salesforce_contract_number}`
          : null,
        r.value != null ? formatMoneyCompact(r.value) : null,
        r.description,
      ]);
    case "contact":
      return joinDot([r.title, r.customer_name]);
    case "account":
      return joinDot([r.account_name, r.customer_name]);
  }
}

interface SearchResultRowProps {
  result: SearchResult;
  /** Unique cmdk item value — cmdk keys its keyboard selection off this. */
  value: string;
  onSelect: () => void;
}

/**
 * One result rendered as a cmdk `CommandItem` — cmdk owns keyboard navigation,
 * the `data-[selected]` highlight, and listbox/option a11y roles. This component
 * only supplies the visual: kind icon, label, a deal stage badge, the context
 * sub-line, and the kind badge.
 */
export function SearchResultRow({ result, value, onSelect }: SearchResultRowProps) {
  const Icon = KIND_ICON[result.kind];
  const sub = subline(result);
  const stage =
    result.kind === "deal" && result.deal_state ? (result.deal_state as DealState) : null;

  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className="group items-start gap-2.5 rounded-none px-3 py-2 data-[selected=true]:bg-primary-dim"
    >
      <Icon
        size={16}
        className="mt-0.5 shrink-0 text-muted-foreground group-data-[selected=true]:text-primary"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm text-foreground">{result.label}</span>
          {stage && (
            <Badge variant={STAGE_VARIANT[stage] ?? "default"} className="shrink-0">
              {DEAL_STATE_LABELS[stage] ?? stage}
            </Badge>
          )}
        </span>
        {sub && <span className="truncate text-xs text-text-dim">{sub}</span>}
      </span>
      <Badge
        variant={result.kind === "customer" ? "primary" : "default"}
        className="ml-auto shrink-0 self-start"
      >
        {KIND_LABEL[result.kind]}
      </Badge>
    </CommandItem>
  );
}
